import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import { Spinner, Alert, Table, Pagination } from "react-bootstrap";
import { 
  Edit, Users, Calendar, Mail, Phone,
  BookOpen, Award, TrendingUp, Monitor, Briefcase, GraduationCap,
  Clock, ExternalLink, ChevronRight, Video, VideoOff
} from "lucide-react";
import {
  UserAvatar, StatusBadge, EmptyState, ProgressBar,
  AppBadge, AppButton, BackButton, ConfirmModal,
  AppCard, AppCardHeader, AppCardTitle, AppCardContent,
} from "../ui";
import { getCurrentUser } from "../../utils/auth";
import CommonToaster from "../Common/CommonToaster";
import PreMeetingCheck from "../Classroom/PreMeetingCheck";
import { FaKey } from "react-icons/fa";
import "./InstructorProfilePage.scss";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata",
  });
};

const formatTime = (time24) => {
  if (!time24) return "";
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
};

/* ─── Info Row ─── */
function InfoRow({ icon: Icon, label, value, href }) {
  return (
    <div className="info-row">
      <div className="info-row__icon">
        <Icon size={16} />
      </div>
      <div className="info-row__text">
        <p className="info-row__label">{label}</p>
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className="info-row__link">
            View Profile <ExternalLink size={12} />
          </a>
        ) : (
          <p className="info-row__value">{value || "-"}</p>
        )}
      </div>
    </div>
  );
}

/* ─── KPI Card ─── */
function KpiCard({ icon: Icon, label, value, subtitle, color = "primary" }) {
  return (
    <AppCard>
      <AppCardContent className="kpi-card__content">
        <div className={`kpi-card__icon kpi-card__icon--${color}`}>
          <Icon size={20} />
        </div>
        <div className="kpi-card__text">
          <p className="kpi-card__label">{label}</p>
          <p className="kpi-card__value">{value}</p>
          {subtitle && <p className="kpi-card__subtitle">{subtitle}</p>}
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ─── Assigned Batches Table ─── */
function AssignedBatchesTable({ batches, instructorId, onStartMeeting, onEndMeeting, isInstructorView }) {
  const navigate = useNavigate();
  
  const handleBatchClick = (batchId) => {
    if (isInstructorView) {
      navigate(`/i/batch/${batchId}`);
    } else {
      navigate(`/batches/${batchId}`);
    }
  };

  if (!batches || batches.length === 0) {
    return (
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <Monitor size={16} />
            Active Batches
          </AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <EmptyState 
            icon={<Monitor />} 
            title="No active batches" 
            description="No batches currently assigned"
          />
        </AppCardContent>
      </AppCard>
    );
  }

  const ongoingBatches = batches.filter(b => b.status === "Ongoing");

  const calculateProgress = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const now = Date.now();
    if (now < start) return 0;
    if (now > end) return 100;
    return Math.round(((now - start) / (end - start)) * 100);
  };

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          <Monitor size={16} />
          Active Batches
          <AppBadge variant="secondary" size="sm">{ongoingBatches.length}</AppBadge>
        </AppCardTitle>
      </AppCardHeader>
      <div className="ip-table-wrap">
        <Table className="ip-table" hover>
          <thead>
            <tr>
              <th>Batch</th>
              <th>Course</th>
              <th>Schedule</th>
              <th>Mode</th>
              <th className="text-center">Students</th>
              <th className="text-center">Attendance</th>
              <th>Progress</th>
              <th>Status</th>
              {isInstructorView && <th className="text-center">Class</th>}
              <th style={{ width: 44 }}></th>
            </tr>
          </thead>
          <tbody>
            {batches.map((batch) => {
              const progress = calculateProgress(batch.startDate, batch.endDate);
              return (
                <tr key={batch._id}>
                  <td>
                    <span 
                      className="ip-table__link" 
                      onClick={() => handleBatchClick(batch._id)}
                    >
                      {batch.name}
                    </span>
                  </td>
                  <td className="ip-table__muted">{batch.course?.name || "-"}</td>
                  <td>
                    <div className="ip-table__schedule-time">
                      {batch.startTime && batch.endTime 
                        ? `${formatTime(batch.startTime)} - ${formatTime(batch.endTime)}` 
                        : "-"}
                    </div>
                    <div className="ip-table__schedule-date">
                      {formatDate(batch.startDate)} - {formatDate(batch.endDate)}
                    </div>
                  </td>
                  <td>
                    <AppBadge variant="outline">{batch.mode || "Online"}</AppBadge>
                  </td>
                  <td className="text-center">
                    <span className="fw-medium">{batch.studentCount ?? batch.strength ?? 0}</span>
                    <span className="ip-table__muted">/{batch.effectiveMaxStudents || batch.maxStudents || '∞'}</span>
                  </td>
                  <td className="text-center">
                    <span className={`fw-medium ${(batch.avgAttendance || 0) >= 80 ? 'ip-text-emerald' : 'ip-text-amber'}`}>
                      {batch.avgAttendance || 0}%
                    </span>
                  </td>
                  <td>
                    <div className="ip-table__progress">
                      <ProgressBar value={progress} size="sm" className="ip-table__progress-bar" />
                      <span className="ip-table__progress-text">{progress}%</span>
                    </div>
                  </td>
                  <td>
                    <StatusBadge status={batch.status} />
                  </td>
                  {isInstructorView && (
                    <td className="text-center">
                      {(batch.mode === 'Online' || batch.mode === 'Both' || batch.meetingLink || batch.zoomMeetingId) && !batch.isLive && (
                        <AppButton
                          variant="primary"
                          size="sm"
                          className="ip-class-btn ip-class-btn--start"
                          onClick={(e) => { e.stopPropagation(); onStartMeeting(batch); }}
                        >
                          <Video size={14} />
                          Start Class
                        </AppButton>
                      )}
                      {(batch.mode === 'Online' || batch.mode === 'Both' || batch.meetingLink || batch.zoomMeetingId) && batch.isLive && (
                        <div className="d-flex align-items-center gap-1 justify-content-center">
                          <AppButton
                            variant="primary"
                            size="sm"
                            className="ip-class-btn ip-class-btn--rejoin"
                            onClick={(e) => { e.stopPropagation(); window.location.href = `/i/classroom/${batch._id}`; }}
                          >
                            <Video size={14} />
                            Rejoin
                          </AppButton>
                          <AppButton
                            variant="destructive"
                            size="sm"
                            className="ip-class-btn ip-class-btn--end"
                            onClick={(e) => { e.stopPropagation(); onEndMeeting(batch); }}
                          >
                            <VideoOff size={14} />
                            End Class
                          </AppButton>
                        </div>
                      )}
                      {batch.mode !== 'Online' && batch.mode !== 'Both' && !batch.meetingLink && !batch.zoomMeetingId && (
                        <span className="ip-table__muted" style={{ fontSize: '0.75rem' }}>No link</span>
                      )}
                    </td>
                  )}
                  <td>
                    <AppButton 
                      variant="ghost"
                      size="icon"
                      onClick={() => handleBatchClick(batch._id)}
                    >
                      <ChevronRight size={16} />
                    </AppButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </AppCard>
  );
}

/* ─── Weekly Schedule ─── */
function WeeklySchedule({ schedule }) {
  const allDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          <Clock size={16} />
          Weekly Schedule
        </AppCardTitle>
      </AppCardHeader>
      <AppCardContent>
        <div className="ip-schedule">
          {allDays.map((day) => {
            const daySchedule = schedule.find((s) => s.day === day);
            const hasBatches = daySchedule && daySchedule.batches.length > 0;
            
            return (
              <div 
                key={day} 
                className={`ip-schedule__row ${hasBatches ? 'ip-schedule__row--active' : ''}`}
              >
                <span className={`ip-schedule__day ${!hasBatches ? 'ip-schedule__day--empty' : ''}`}>
                  {day.slice(0, 3)}
                </span>
                {hasBatches ? (
                  <div className="ip-schedule__batches">
                    {daySchedule.batches.map((b, i) => (
                      <div key={i} className="ip-schedule__batch">
                        <AppBadge variant="secondary" size="sm">{b.name}</AppBadge>
                        <span className="ip-schedule__time">{b.time}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="ip-schedule__empty">No classes</span>
                )}
              </div>
            );
          })}
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ─── Upcoming Interviews ─── */
function UpcomingInterviews({ interviews }) {
  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          <Briefcase size={16} />
          Upcoming Interviews
          {interviews.length > 0 && <AppBadge variant="secondary" size="sm">{interviews.length}</AppBadge>}
        </AppCardTitle>
      </AppCardHeader>
      <AppCardContent>
        {interviews.length === 0 ? (
          <EmptyState 
            icon={<Briefcase />} 
            title="No upcoming interviews" 
            description="No interviews scheduled" 
          />
        ) : (
          <div className="ip-interviews">
            {interviews.map((interview) => (
              <div key={interview._id} className="ip-interviews__item">
                <div className="ip-interviews__info">
                  <p className="ip-interviews__name">{interview.studentName}</p>
                  <p className="ip-interviews__detail">{interview.company} - {interview.round}</p>
                </div>
                <div className="ip-interviews__date">
                  <p className="ip-interviews__name">{formatDate(interview.date)}</p>
                  <p className="ip-interviews__detail">{interview.time}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </AppCardContent>
    </AppCard>
  );
}


/* ═══════════════════════════════════════════════════ */
/* ─── Main Component ─── */
/* ═══════════════════════════════════════════════════ */
const InstructorProfilePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [batchesPage, setBatchesPage] = useState(1);
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });
    const [resettingPwd, setResettingPwd] = useState(false);
    const [activeTab, setActiveTab] = useState("batches");
    const [confirmAction, setConfirmAction] = useState(null); // { type, batch?, title, message, variant, confirmText }
    const [startMeetingBatch, setStartMeetingBatch] = useState(null);

    let userId = id || null;
    const user = getCurrentUser();
    const isSuperAdmin = user?.usertype === "superadmin";
    const isInstructorView = user?.usertype === "instructor";
    
    if (user && user.usertype === "instructor" && user.instructorId) {
        userId = user.instructorId;
    }

    const [batchesData, setBatchesData] = useState({ batches: [], total: 0, totalPages: 1 });

    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true);
            setErr("");
            try {
                const { data } = await apiClient.get(`/instructors/${userId}`);
                setProfile(data);
            } catch {
                setErr("Failed to load instructor details.");
            }
            setLoading(false);
        };
        fetchDetails();
    }, [userId]);

    const fetchBatches = async () => {
        try {
            const { data } = await apiClient.get(`/instructors/${userId}/batches?page=${batchesPage}&pageSize=10`);
            setBatchesData(data);
        } catch {
            setBatchesData({ batches: [], total: 0, totalPages: 1 });
        }
    };

    useEffect(() => {
        fetchBatches();

        // Refetch when page is restored from bfcache (e.g. returning from classroom)
        const handlePageShow = (e) => {
            if (e.persisted) fetchBatches();
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, [userId, batchesPage]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleResetPassword = async () => {
      setConfirmAction(null);
      setResettingPwd(true);
      try {
        const { data } = await apiClient.post(`/instructors/${profile.instructor._id}/reset-password`);
        setToast({ show: true, message: data.message, type: "success" });
      } catch (err) {
        const msg = err?.response?.data?.message || "Failed to reset password.";
        setToast({ show: true, message: msg, type: "danger" });
      } finally {
        setResettingPwd(false);
      }
    };

    const startMeeting = (batch) => {
        if (!batch) return;
        apiClient.post(`/batches/${batch._id}/start-meeting`)
            .then(({ data }) => {
                setToast({ show: true, message: "Meeting started successfully.", type: "success" });

                // V2 / platformHosted: full-page nav to embedded classroom; V1: open plain Zoom link
                if (data.zoomMode === 'v2' || data.zoomMode === 'platformHosted') {
                    window.location.href = `/i/classroom/${batch._id}`;
                } else if (batch.meetingLink) {
                    window.open(batch.meetingLink, "_blank");
                }

                // Refresh both profile upcomingBatches and batchesData
                setProfile(prev => ({
                    ...prev,
                    upcomingBatches: (prev.upcomingBatches || []).map(b =>
                        b._id === batch._id ? { ...b, isLive: true } : b
                    )
                }));
                setBatchesData(prev => ({
                    ...prev,
                    batches: prev.batches.map(b =>
                        b._id === batch._id ? { ...b, isLive: true } : b
                    )
                }));
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || "Failed to start meeting.";
                setToast({ show: true, message: msg, type: "danger" });
            });
    };

    const endMeeting = (batch) => {
        if (!batch) return;
        apiClient.post(`/batches/${batch._id}/end-meeting`)
            .then(() => {
                setToast({ show: true, message: "Meeting ended successfully.", type: "success" });
                setProfile(prev => ({
                    ...prev,
                    upcomingBatches: (prev.upcomingBatches || []).map(b =>
                        b._id === batch._id ? { ...b, isLive: false } : b
                    )
                }));
                setBatchesData(prev => ({
                    ...prev,
                    batches: prev.batches.map(b =>
                        b._id === batch._id ? { ...b, isLive: false } : b
                    )
                }));
            })
            .catch(() => {
                setToast({ show: true, message: "Failed to end meeting.", type: "danger" });
            });
    };

    if (loading)
        return <div className="ip-loading"><Spinner animation="border" /></div>;
    if (err)
        return <Alert variant="danger" className="ip-error">{err}</Alert>;
    if (!profile)
        return <div className="ip-empty-msg">Instructor not found.</div>;

    const { instructor, upcomingBatches = [], upcomingInterviews = [] } = profile;
    
    // Calculate stats
    const totalBatches = batchesData.total || 0;
    const activeBatches = batchesData.batches.filter(b => b.status === "Ongoing").length;
    const completedBatches = batchesData.batches.filter(b => b.status === "Completed").length;
    const totalStudents = upcomingBatches.reduce((sum, b) => sum + (b.strength || 0), 0);

    // Parse skills
    const skills = Array.isArray(instructor.otherSkills)
      ? instructor.otherSkills
      : (instructor.otherSkills || "").split(",").map((s) => s.trim()).filter(Boolean);

    // Specialization line
    const specialization = skills.length > 0 ? skills.slice(0, 3).join(", ") : (instructor.subject || "");

    // Create weekly schedule from upcoming batches
    const weeklySchedule = [];
    if (upcomingBatches.length > 0) {
      const daysMap = {};
      upcomingBatches.forEach(batch => {
        const days = batch.batchType === "WEEKEND" 
          ? ["Saturday", "Sunday"] 
          : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
        days.forEach(day => {
          if (!daysMap[day]) daysMap[day] = [];
          daysMap[day].push({ 
            name: batch.name, 
            time: batch.startTime && batch.endTime 
              ? `${batch.startTime}-${batch.endTime}` 
              : batch.startTime || "-"
          });
        });
      });
      Object.keys(daysMap).forEach(day => {
        weeklySchedule.push({ day, batches: daysMap[day] });
      });
    }

    return (
        <div className="ip-page">
            {/* ─── Top Bar ─── */}
            <div className="ip-topbar">
                <div className="ip-topbar__left">
                    <BackButton onClick={() => navigate(-1)} />
                    <div>
                        <h1 className="ip-topbar__name">{instructor.name}</h1>
                        <p className="ip-topbar__sub">{specialization}</p>
                    </div>
                </div>
                <div className="ip-topbar__right">
                    <StatusBadge status={instructor.status} />
                    {isSuperAdmin && instructor._id && (
                      <AppButton
                        variant="outline"
                        size="sm"
                        className="ip-btn-reset"
                        onClick={() => setConfirmAction({
                            type: 'resetPassword',
                            title: 'Reset Password?',
                            message: `Reset password for ${instructor.name} to default (123456)?`,
                            variant: 'warning',
                            confirmText: 'Reset Password',
                        })}
                        disabled={resettingPwd}
                      >
                        {resettingPwd ? <Spinner animation="border" size="sm" /> : <FaKey size={12} />}
                        Reset Password
                      </AppButton>
                    )}
                    <AppButton 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(isInstructorView ? '/i/account-settings' : `/instructors/edit/${userId}`)}
                    >
                        <Edit size={14} />
                        Edit Profile
                    </AppButton>
                </div>
            </div>

            {/* ─── Profile Overview (2 column) ─── */}
            <div className="ip-profile-grid">
                {/* Left: Profile Card spanning 2/3 */}
                <div className="ip-profile-main">
                    <AppCard>
                        <AppCardContent className="ip-profile-content">
                            {/* Avatar column */}
                            <div className="ip-avatar-col">
                                <UserAvatar 
                                  name={instructor.name} 
                                  size="xl" 
                                  src={instructor.avatar}
                                />
                            </div>

                            {/* Info column */}
                            <div className="ip-info-col">
                                {instructor.bio && (
                                  <p className="ip-bio">{instructor.bio}</p>
                                )}
                                <div className="ip-info-grid">
                                    <InfoRow icon={Mail} label="Email" value={instructor.email} />
                                    <InfoRow icon={Phone} label="Phone" value={instructor.mobile} />
                                    <InfoRow icon={Briefcase} label="Experience" value={instructor.experience ? `${instructor.experience} years` : "-"} />
                                    <InfoRow icon={GraduationCap} label="Qualification" value={instructor.qualification} />
                                    <InfoRow icon={Calendar} label="Date of Birth" value={formatDate(instructor.dob)} />
                                    <InfoRow icon={Calendar} label="Joined" value={formatDate(instructor.joinedDate)} />
                                    {instructor.linkedin && (
                                      <InfoRow icon={ExternalLink} label="LinkedIn" href={instructor.linkedin} />
                                    )}
                                </div>
                            </div>
                        </AppCardContent>
                    </AppCard>
                </div>

                {/* Right: Skills & Stats Card spanning 1/3 */}
                <div className="ip-profile-side">
                    <AppCard>
                        <AppCardContent className="ip-side-content">
                            <div className="ip-side-section">
                                <p className="ip-side__label">Courses Teaching</p>
                                <div className="ip-side__badges">
                                    <AppBadge variant="default">{instructor.subject || "N/A"}</AppBadge>
                                </div>
                            </div>

                            <div className="ip-side-section">
                                <p className="ip-side__label">Skills & Expertise</p>
                                <div className="ip-side__badges">
                                    {skills.length > 0 ? (
                                      skills.map((skill, i) => (
                                        <AppBadge key={i} variant="outline">{skill}</AppBadge>
                                      ))
                                    ) : (
                                      <span className="ip-side__empty">No skills listed</span>
                                    )}
                                </div>
                            </div>

                            <div className="ip-side-stats">
                                <div className="ip-side-stat">
                                    <span className="ip-side-stat__label">Total Batches</span>
                                    <span className="ip-side-stat__value">{totalBatches}</span>
                                </div>
                                <div className="ip-side-stat">
                                    <span className="ip-side-stat__label">Active Batches</span>
                                    <span className="ip-side-stat__value ip-side-stat__value--green">{activeBatches}</span>
                                </div>
                                <div className="ip-side-stat">
                                    <span className="ip-side-stat__label">Completed Batches</span>
                                    <span className="ip-side-stat__value">{completedBatches}</span>
                                </div>
                                <div className="ip-side-stat">
                                    <span className="ip-side-stat__label">Total Students</span>
                                    <span className="ip-side-stat__value">{totalStudents}</span>
                                </div>
                            </div>
                        </AppCardContent>
                    </AppCard>
                </div>
            </div>

            {/* ─── KPI Cards Row ─── */}
            <div className="ip-kpi-grid">
                <KpiCard icon={Users} label="Total Students" value={totalStudents} subtitle="Across batches" color="primary" />
                <KpiCard icon={BookOpen} label="Active Batches" value={activeBatches} subtitle={`of ${totalBatches} total`} color="blue" />
                <KpiCard icon={Calendar} label="Upcoming Classes" value={upcomingBatches.length} subtitle="Today / tomorrow" color="amber" />
                <KpiCard icon={TrendingUp} label="Completed Batches" value={completedBatches} color="emerald" />
                <KpiCard icon={Award} label="Total Batches" value={totalBatches} subtitle="All time" color="violet" />
            </div>

            {/* ─── Tabs ─── */}
            <div className="ip-tabs">
                <div className="ip-tabs__list">
                    <button 
                        className={`ip-tabs__trigger ${activeTab === 'batches' ? 'ip-tabs__trigger--active' : ''}`}
                        onClick={() => setActiveTab('batches')}
                    >
                        Batches
                    </button>
                    <button 
                        className={`ip-tabs__trigger ${activeTab === 'schedule' ? 'ip-tabs__trigger--active' : ''}`}
                        onClick={() => setActiveTab('schedule')}
                    >
                        Schedule
                    </button>
                    <button 
                        className={`ip-tabs__trigger ${activeTab === 'performance' ? 'ip-tabs__trigger--active' : ''}`}
                        onClick={() => setActiveTab('performance')}
                    >
                        Performance
                    </button>
                </div>

                {/* Batches Tab */}
                {activeTab === 'batches' && (
                    <div className="ip-tabs__panel">
                        <AssignedBatchesTable 
                            batches={batchesData.batches} 
                            instructorId={userId}
                            onStartMeeting={(batch) => setStartMeetingBatch(batch)}
                            onEndMeeting={(batch) => setConfirmAction({
                                type: 'endMeeting',
                                batch,
                                title: 'End Class?',
                                message: 'This will end the meeting for all participants. Are you sure you want to end the class?',
                                variant: 'warning',
                                confirmText: 'End Class',
                            })}
                            isInstructorView={user?.usertype === "instructor"}
                        />
                        
                        {batchesData.totalPages > 1 && (
                          <div className="d-flex justify-content-end mt-3">
                            <Pagination>
                              <Pagination.Prev 
                                onClick={() => setBatchesPage(p => p - 1)} 
                                disabled={batchesPage === 1} 
                              />
                              {[...Array(batchesData.totalPages).keys()].map(i => (
                                <Pagination.Item
                                  key={i + 1}
                                  active={batchesPage === i + 1}
                                  onClick={() => setBatchesPage(i + 1)}
                                >
                                  {i + 1}
                                </Pagination.Item>
                              ))}
                              <Pagination.Next 
                                onClick={() => setBatchesPage(p => p + 1)} 
                                disabled={batchesPage === batchesData.totalPages} 
                              />
                            </Pagination>
                          </div>
                        )}
                    </div>
                )}

                {/* Schedule Tab */}
                {activeTab === 'schedule' && (
                    <div className="ip-tabs__panel">
                        <div className="ip-schedule-grid">
                            <WeeklySchedule schedule={weeklySchedule} />
                            <UpcomingInterviews interviews={upcomingInterviews} />
                        </div>
                    </div>
                )}

                {/* Performance Tab */}
                {activeTab === 'performance' && (
                    <div className="ip-tabs__panel">
                        <div className="ip-perf-placeholder">
                            <EmptyState
                              icon={<TrendingUp />}
                              title="Performance analytics"
                              description="Performance charts and analytics will appear here"
                            />
                        </div>
                    </div>
                )}
            </div>

            <CommonToaster
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast((t) => ({ ...t, show: false }))}
            />

            <PreMeetingCheck
                show={!!startMeetingBatch}
                role="instructor"
                batchName={startMeetingBatch?.name}
                batchId={startMeetingBatch?._id}
                confirmText="Start Class"
                onReady={() => { const b = startMeetingBatch; setStartMeetingBatch(null); startMeeting(b); }}
                onCancel={() => setStartMeetingBatch(null)}
            />

            <ConfirmModal
                show={!!confirmAction}
                onHide={() => setConfirmAction(null)}
                onConfirm={() => {
                    if (!confirmAction) return;
                    if (confirmAction.type === 'resetPassword') handleResetPassword();
                    else if (confirmAction.type === 'endMeeting') { setConfirmAction(null); endMeeting(confirmAction.batch); }
                }}
                title={confirmAction?.title || ''}
                message={confirmAction?.message || ''}
                confirmText={confirmAction?.confirmText || 'Confirm'}
                cancelText="Cancel"
                variant={confirmAction?.variant || 'info'}
            />
        </div>
    );
};

export default InstructorProfilePage;
